import React, { useState, useEffect, useRef, useCallback } from "react";
import { DateRange } from "react-date-range";
import moment from "moment";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { warningPopup } from "../helpers";

const DetailDatePicker = ({
  setStartDate,
  startDate,
  endDate,
  setEndDate,
  disable = [],
  maxDays,
  minDays,
}) => {
  const [range, setRange] = useState([
    {
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      key: "selection",
    },
  ]);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  // Check if a date is disabled (blocked or in the past)
  const isDateDisabled = useCallback(
    (date) => {
      if (!date) return true;
      const momentDate = moment(date).startOf("day"); // Normalize to start of day for accurate comparison
      const today = moment().startOf("day");

      // 1. Check if date is in the past
      if (momentDate.isBefore(today)) {
        return true; // All past dates are always disabled
      }

      // 2. Check if date falls within any blocked range (adjusted for future end-dates)
      return disable.some((blockedRange) => {
        const start = moment(blockedRange.start_date).startOf("day");
        let end = moment(blockedRange.end_date).startOf("day");

        // Apply "subtract one day" only if the end date is today or in the future
        // This ensures past end_dates (if any in your data) remain fully blocked for their duration
        if (end.isSameOrAfter(today)) {
          end = end.subtract(1, "day");
        }

        // The date is disabled if it's between the (possibly adjusted) blocked range
        return momentDate.isBetween(start, end, "day", "[]");
      });
    },
    [disable]
  );

  // Handle date selection changes
  const handleDateChange = (item) => {
    const { startDate: newStartDate, endDate: newEndDate } = item.selection;

    // Case 1: Selecting start date
    if (!range[0].startDate || (range[0].startDate && range[0].endDate)) {
      if (!isDateDisabled(newStartDate)) {
        setRange([
          { startDate: newStartDate, endDate: null, key: "selection" },
        ]);
        setStartDate(moment(newStartDate).format("YYYY-MM-DD"));
        setEndDate(null);
      } else {
        warningPopup("This date is not available for selection");
      }
    }
    // Case 2: Selecting end date
    else if (range[0].startDate && !range[0].endDate && newEndDate) {
      // Check if any date in the range is disabled
      const startMoment = moment(range[0].startDate);
      const endMoment = moment(newEndDate);
      const daysDiff = endMoment.diff(startMoment, "days");

      let hasDisabledDates = false;
      for (let i = 0; i <= daysDiff; i++) {
        const currentDate = startMoment.clone().add(i, "days").toDate();
        if (isDateDisabled(currentDate)) {
          hasDisabledDates = true;
          break;
        }
      }

      if (!hasDisabledDates) {
        setRange([{ ...range[0], endDate: newEndDate }]);
        setEndDate(moment(newEndDate).format("YYYY-MM-DD"));
        setIsCalendarOpen(false);
      } else {
        warningPopup("The selected range contains unavailable dates");
      }
    }
  };

  // Check if selected range meets min/max requirements
  const checkBookableRange = useCallback(() => {
    if (!startDate || !endDate) return;

    const daysDifference = moment(endDate).diff(moment(startDate), "days") + 1;
    if (daysDifference > maxDays || daysDifference < minDays) {
      warningPopup(
        `Please select a range between ${minDays} and ${maxDays} days.`
      );
      setStartDate(null);
      setEndDate(null);
    }
  }, [endDate, maxDays, minDays, setEndDate, setStartDate, startDate]);

  useEffect(() => {
    checkBookableRange();
  }, [checkBookableRange]);

  // Render custom day content with appropriate styling
  const renderDayContents = (day) => {
    const date = moment(day);
    const isDisabled = isDateDisabled(day);
    const isStartDate =
      range[0].startDate && date.isSame(range[0].startDate, "day");
    const isEndDate = range[0].endDate && date.isSame(range[0].endDate, "day");
    const isInRange =
      range[0].startDate &&
      range[0].endDate &&
      date.isBetween(range[0].startDate, range[0].endDate, "day", "[]");

    let style = {
      backgroundColor: "transparent",
      color: isDisabled ? "#ccc" : "inherit",
    };

    if (isDisabled) {
      style = { backgroundColor: "#fde2e1", color: "#ccc" };
    } else if (isStartDate || isEndDate || isInRange) {
      style = { backgroundColor: "#54C4D9", color: "#ffffff" };
    }

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
          borderRadius: "20%",
        }}
      >
        {date.date()}
      </div>
    );
  };

  return (
    <div className="calendar-containers">
      <div className="date-display">
        <input
          className="date_picker_place"
          type="text"
          value={
            range[0].startDate
              ? moment(range[0].startDate).format("YYYY-MM-DD")
              : ""
          }
          placeholder="CHECK-IN"
          readOnly
          onClick={() => setIsCalendarOpen(true)}
        />
        <input
          className="date_picker_place"
          type="text"
          value={
            range[0].endDate
              ? moment(range[0].endDate).format("YYYY-MM-DD")
              : ""
          }
          placeholder="CHECK-OUT"
          readOnly
          onClick={() => setIsCalendarOpen(true)}
        />
      </div>
      {isCalendarOpen && (
        <div className="picker" ref={calendarRef}>
          <DateRange
            ranges={range}
            onChange={handleDateChange}
            showDateDisplay={false}
            direction="horizontal"
            dayContentRenderer={renderDayContents}
            moveRangeOnFirstSelection={false}
            minDate={new Date()}
            disabledDay={isDateDisabled}
          />
        </div>
      )}
    </div>
  );
};

export default DetailDatePicker;
